/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import NavbarLangOnly from "../../components/Home/navbarLang/NavbarLangOnly";
import iraqFlag from "../../assets/iraq-flag.png";
import SignupUI from "./SignupUI";
import { useAuth } from "../../context/auth/AuthContext";
import {
  getOnboardingResumePath,
  hasOnboardingSession,
  isAccountExistsError,
  loadSignupDraft,
  loginExistingTenant,
  persistTenantAuth,
  recoverTenantAuth,
  saveSignupDraft,
  setOnboardingStep,
  clearSignupDraft,
} from "../utils/onboardingSession";

export default function Signup() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const { login: authLogin, isAuthenticated, authRole, isLoading } = useAuth();

  const resumeOnboarding = () => {
    recoverTenantAuth(authLogin);
    navigate(getOnboardingResumePath());
  };

  /* 🔄 Auto Redirect if Logged In or resuming registration */
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated()) {
      if (authRole === "superAdmin") navigate("/admin");
      else if (authRole === "tenant") resumeOnboarding();
      else navigate("/");
      return;
    }
    if (hasOnboardingSession()) {
      resumeOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, authRole, navigate]);

  const [form, setForm] = useState(() => {
    const draft = loadSignupDraft() || {};
    return {
      fullName: draft.fullName || "",
      engFullName: draft.engFullName || "",
      phone: draft.phone || "",
      email: draft.email || "",
      password: "",
      confirmPassword: "",
      agreeTerms: false,
    };
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const COLORS = {
    darkPurple: "#21003D",
    purple: "#7400BF",
    gold: "#F2B441",
    gradient: "linear-gradient(135deg, #21003D, #7400BF)",
  };

  const notify = (type, msg) =>
    toast[type](msg, {
      position: "top-center",
      rtl: isArabic,
      theme: "colored",
      autoClose: 2500,
    });

  /* 🔐 قوة كلمة السر */
  useEffect(() => {
    const val = form.password;
    if (!val) return setPasswordStrength("");

    const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;
    const medium = /^(?=.*[A-Za-z])(?=.*\d).{6,}$/;

    if (strong.test(val)) setPasswordStrength(isArabic ? "قوية" : "Strong");
    else if (medium.test(val)) setPasswordStrength(isArabic ? "متوسطة" : "Medium");
    else setPasswordStrength(isArabic ? "ضعيفة" : "Weak");
  }, [form.password, isArabic]);

  const getStrengthColor = () =>
    passwordStrength.match(/Strong|قوية/) ? "#16a34a" :
      passwordStrength.match(/Medium|متوسطة/) ? "#f97316" :
        passwordStrength ? "#dc2626" : "#d1d5db";

  const getStrengthValue = () =>
    passwordStrength.match(/Strong|قوية/) ? "100%" :
      passwordStrength.match(/Medium|متوسطة/) ? "60%" :
        passwordStrength ? "30%" : "0%";

  /* ✅ التحقق من الحقول */
  const validate = () => {
    const e = {};
    const phoneR = /^7\d{9}$/; // رقم يبدأ بـ 7
    const mailR = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passR = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!form.fullName.trim())
      e.fullName = isArabic ? "يرجى إدخال الاسم بالعربية" : "Arabic name required";

    if (!form.engFullName.trim())
      e.engFullName = isArabic ? "يرجى إدخال الاسم بالإنجليزية" : "English name required";

    if (!phoneR.test(form.phone))
      e.phone = isArabic ? "رقم الهاتف غير صحيح" : "Invalid phone";

    if (!mailR.test(form.email))
      e.email = isArabic ? "بريد غير صحيح" : "Invalid email";

    if (!passR.test(form.password))
      e.password = isArabic ? "كلمة السر ضعيفة" : "Weak password";

    if (form.password !== form.confirmPassword)
      e.confirmPassword = isArabic ? "كلمتا السر غير متطابقتين" : "Passwords do not match";

    if (!form.agreeTerms)
      e.agreeTerms = isArabic ? "يجب الموافقة على سياسة الخصوصية وشروط الاستخدام" : "You must agree to the Privacy Policy and Terms of Use";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setForm((prev) => {
      const next = { ...prev, [name]: nextValue };
      saveSignupDraft(next);
      return next;
    });
  };

  /* =====================================================
     🚀 النسخة النهائية الصحيحة من handleSubmit
     ===================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      notify("error", isArabic ? "تحقق من الحقول" : "Fix the form");
      return;
    }

    setLoading(true);

    try {
      const body = {
        tenantName: form.fullName,
        engTenantName: form.engFullName,
        phone: form.phone,
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
      };

      const res = await fetch(`${baseUrl}/Register/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });

      const contentType = res.headers.get("content-type") || "";
      const data = contentType.includes("application/json") ? await res.json() : await res.text();

      if (!res.ok) {
        const message = typeof data === "string" ? data : data?.message || data?.Message || `HTTP ${res.status}`;

        if (isAccountExistsError(message)) {
          try {
            const loginData = await loginExistingTenant(form.email, form.password, baseUrl);
            persistTenantAuth(loginData, authLogin, { source: "login" });
            clearSignupDraft();
            setOnboardingStep(getOnboardingResumePath(loginData?.tenantDetails) === "/theme-plan" ? "theme-plan" : "company-info");
            notify(
              "success",
              isArabic
                ? "هذا الحساب موجود مسبقاً — جارٍ استكمال إنشاء المتجر"
                : "Account already exists — continuing store setup"
            );
            setLoading(false);
            setTimeout(() => navigate(getOnboardingResumePath(loginData?.tenantDetails)), 800);
            return;
          } catch (loginErr) {
            setLoading(false);
            notify(
              "error",
              isArabic
                ? "هذا البريد مسجّل مسبقاً. سجّل الدخول بنفس كلمة السر لمتابعة إنشاء المتجر."
                : "This email is already registered. Sign in with the same password to continue setup."
            );
            return;
          }
        }

        setLoading(false);
        notify("error", message);
        return;
      }

      persistTenantAuth(typeof data === "object" ? data : {}, authLogin, { source: "register" });
      clearSignupDraft();
      setOnboardingStep("company-info");
      setLoading(false);

      notify(
        "success",
        isArabic ? "تم إنشاء الحساب بنجاح" : "Account created successfully"
      );

      setTimeout(() => navigate("/company-info"), 800);
    } catch (error) {
      setLoading(false);
      notify("error", isArabic ? "فشل الاتصال بالخادم" : "Network error");
    }
  };

  return (
    <>
      <NavbarLangOnly background="#112758" />
      <ToastContainer />

      <SignupUI
        COLORS={COLORS}
        isArabic={isArabic}
        isMobile={isMobile}
        form={form}
        errors={errors}
        passwordStrength={passwordStrength}
        getStrengthColor={getStrengthColor}
        getStrengthValue={getStrengthValue}
        loading={loading}
        iraqFlag={iraqFlag}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
    </>
  );
}
